import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import thTranslations from '../translations/th.json';

export type Language = 'th' | 'en' | 'lo';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const fallbackT = (key: string): string => {
  if (!key) return '';
  const keys = key.split('.');
  const lastKey = keys[keys.length - 1];
  return lastKey.charAt(0).toUpperCase() + lastKey.slice(1);
};

const fallbackContext: LanguageContextType = {
  language: 'th',
  setLanguage: () => {},
  t: fallbackT,
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    return fallbackContext;
  }
  return context;
};

interface LanguageProviderProps {
  children: React.ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    const saved = localStorage.getItem('app-language') as Language;
    return saved && ['th', 'en', 'lo'].includes(saved) ? saved : 'th';
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const initialLoadDone = useRef(false);
  const [translations, setTranslations] = useState<Record<string, any>>(thTranslations);

  // No need for initial language useEffect - we read from localStorage in useState initializer

  useEffect(() => {
    // Only load if not Thai (Thai is already loaded synchronously) or if language changed after init
    if (language === 'th' && !initialLoadDone.current) {
      // Thai translations already loaded synchronously
      setIsLoading(false);
      setIsInitialLoad(false);
      initialLoadDone.current = true;
    } else {
      loadTranslations(language);
    }
    document.documentElement.lang = language;
  }, [language]);

  const loadTranslations = async (lang: Language) => {
    setIsLoading(true);
    try {
      const module = await import(`../translations/${lang}.json`);
      setTranslations(module.default);
      setIsInitialLoad(false);
      initialLoadDone.current = true;
    } catch (error) {
      console.error(`Failed to load translations for ${lang}:`, error);
      if (lang !== 'th') {
        // Fall back to Thai (already loaded synchronously)
        setTranslations(thTranslations);
      }
      setIsInitialLoad(false);
      initialLoadDone.current = true;
    } finally {
      setIsLoading(false);
    }
  };

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem('app-language', lang);
  };

  const t = (key: string): string => {
    if (!key) return '';
    
    const keys = key.split('.');
    let result: any = translations;
    
    for (const k of keys) {
      if (result && typeof result === 'object' && k in result) {
        result = result[k];
      } else {
        const lastKey = keys[keys.length - 1];
        return lastKey.charAt(0).toUpperCase() + lastKey.slice(1);
      }
    }
    
    return typeof result === 'string' ? result : key;
  };

  const contextValue: LanguageContextType = {
    language,
    setLanguage,
    t,
  };

  // Show loading state only during initial load
  if (isInitialLoad && isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  );
};